import React, { useMemo, useState } from 'react'
import ProfilePic from 'components/PMBProfilePic'
import { useIntl } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import {
  useGetPayableQuery,
  useGetPaymentHistoryPersonalQuery,
  useGetPaymentHistoryQuery,
} from './payableNotesApiSlice'
import { setFetchOptionPaymentHistory as setFetchOptionPaymentHistoryCompany, setFetchOptionPaymentHistoryPersonal } from './payableNotesSlice'
import { useDebounce } from 'helpers'
import { formatDatePMB } from 'utils/formatDate'
import { TableComponent } from 'components/TableComponent'
import PMBPagination from 'components/PMBPagination'
import { Badge, Col, Collapse, Row } from 'reactstrap'
import PMBSearchInputGroup from 'components/PMBSearchInputGroup'
import PMBSelect from 'components/PMBSelect'
import {
  Calendar,
  ChevronDown,
  ChevronUp,
  Filter,
  User,
  X,
} from 'react-feather'
import Flatpickr from 'react-flatpickr'
import moment from 'moment-timezone'
import CustomButton from 'components/Buttons/CustomButton'
import SvgIcon from 'components/Icon/SvgIcon'
import { P } from 'components'
import ICTunai from 'assets/icons/currency-dollar.svg'
import ICUangBelanja from 'assets/icons/uang-belanja-wallet.png'
import ExpandableNoteList from 'components/ExpandableNoteList'
import ImageCrash from 'assets/images/404.png'
import PMBModalImagePreview from 'components/PMBModalImagePreview'
import PMBProjectSelections from 'components/PMBProjectSelections'

const PaymentHistory = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const intl = useIntl()

  const [searchInput, setSearchInput] = useState('')
  const [isShowFilter, setIsShowFilter] = useState(false)
  const [modalOpenImage, setModalOpenImage] = useState(false)
  const [selectedRow, setSelectedRow] = useState(null)

  const { tagPaymentHistory: tagPaymentHistoryCompany, tagPaymentHistoryPersonal, type } = useSelector((s) => s.payableNotes)

  const tagPaymentHistory = type === 'company' ? tagPaymentHistoryCompany : tagPaymentHistoryPersonal

  const { data: dataCompany, isLoading: isLoadingCompany, refetch: refetchCompany, status: statusCompany } = useGetPaymentHistoryQuery({
    ...tagPaymentHistoryCompany,
    typeFetch: 'company',
  }, { skip: type === 'personal' })

  const { data: dataPersonal, isLoading: isLoadingPersonal, refetch: refetchPersonal, status: statusPersonal } = useGetPaymentHistoryPersonalQuery({
    ...tagPaymentHistoryPersonal,
    typeFetch: 'personal',
  }, { skip: type === 'company' })

  const data = type === 'company' ? dataCompany : dataPersonal
  const isLoading = type === 'company' ? isLoadingCompany : isLoadingPersonal
  const refetch = type === 'company' ? refetchCompany : refetchPersonal
  const status = type === 'company' ? statusCompany : statusPersonal


  const setFetchOptionPaymentHistory = type === 'personal' ? setFetchOptionPaymentHistoryPersonal : setFetchOptionPaymentHistoryCompany

  const activeFilterCount = useMemo(() => {
    let count = 0
    if (tagPaymentHistory?.type) count++
    if (tagPaymentHistory?.search) count++
    if (tagPaymentHistory?.from || tagPaymentHistory?.to) count++
    if (tagPaymentHistory?.paymentmethod) count++
    if (tagPaymentHistory?.projectId) count++
    return count
  }, [tagPaymentHistory])

  const typeOptions = [
    {
      value: undefined,
      label: 'Semua',
    },
    {
      value: 'utang',
      label: 'Utang',
    },
    {
      value: 'piutang',
      label: 'Piutang',
    },
  ]

  const paymentMethodOptions = [
    {
      value: 'cashpayment',
      label: (
        <div className="d-flex align-items-center gap-2">
          <img width={20} height={20} src={ICTunai} alt="Tunai" /> Tunai
        </div>
      ),
    },
    {
      value: 'paymentviaagent',
      label: (
        <div className="d-flex align-items-center gap-2">
          <User style={{ color: '#007BFF' }} size={20} /> Pembayaran Melalui
          Agen
        </div>
      ),
    },
    {
      value: 'uangbelanja',
      label: (
        <div className="d-flex align-items-center gap-2">
          <img width={20} height={20} src={ICUangBelanja} alt="Uang Belanja" />{' '}
          Uang Belanja
        </div>
      ),
    },
  ]

  const debouncedSearch = useDebounce((value) => {
    dispatch(
      setFetchOptionPaymentHistory({
        page: 0,
        search: value,
      }),
    )
  }, 600)

  const onHandleSearchChange = (value) => {
    setSearchInput(value ?? '')
    debouncedSearch(value ?? '')
  }

  const handleKeyDown = (e) => {
    if (e.key === ' ' && tagPaymentHistory?.search.length === 0) {
      e.preventDefault()
    }
  }

  const handleSort = (column, direction) => {
    dispatch(
      setFetchOptionPaymentHistory({
        sortby: column.sortField,
        sortdir: direction,
      }),
    )
  }

  const handleResetFilter = () => {
    setSearchInput('')
    dispatch(
      setFetchOptionPaymentHistory({
        type: undefined,
        paymentmethod: undefined,
        search: undefined,
        from: undefined,
        to: undefined,
        page: 0,
        projectId: undefined,
      }),
    )
  }

  const columns = [
    {
      name: <span>Tanggal</span>,
      minWidth: '150px',
      sortField: 'createdAt',
      sortable: true,
      selector: (row) => formatDatePMB(row?.createdAt) || '-',
      cell: (row) => <div>{formatDatePMB(row?.createdAt) || '-'}</div>,
    },
    {
      name: <span>Tipe</span>,
      minWidth: '100px',
      cell: (row) => {
        if (row?.type === 'Piutang') {
          return (
            <div className="badge rounded-pill px-2 bg-primary-50 text-primary">
              Piutang
            </div>
          )
        } else if (row?.type === 'Utang') {
          return (
            <div className="badge rounded-pill px-2 bg-warning-50 text-warning-700">
              Utang
            </div>
          )
        }
      },
    },
    {
      name: <span>Metode Pembayaran</span>,
      minWidth: '150px',
      selector: (row) => row?.paymentMethod || '-',
      cell: (row) => {
        if (row?.paymentMethod === 'cashpayment') {
          return (
            <div
              className="badge rounded-pill bg-gray-100 text-gray-700 px-2 py-1 flex-centered gap-1"
              style={{
                whiteSpace: 'normal',
                wordBreak: 'break-word',
                maxWidth: '100%',
              }}
            >
              <img width={18} height={18} src={ICTunai} alt={'Tunai'} /> Tunai
            </div>
          )
        } else if (row?.paymentMethod === 'uangbelanja') {
          return (
            <div
              className={`badge rounded-pill bg-primary-50 px-2 py-1 flex-centered gap-1`}
              style={{
                whiteSpace: 'normal',
                wordBreak: 'break-word',
                maxWidth: '100%',
              }}
            >
              <img
                width={16}
                height={16}
                src={ICUangBelanja}
                alt={'Uang Belanja'}
              />
              <span className={'text-primary'}>Uang Belanja</span>
            </div>
          )
        } else if (row?.paymentMethod === 'paymentviaagent') {
          return (
            <div
              className="badge rounded-pill text-primary-v2 bg-primary-50 px-2 py-1 flex-centered gap-1"
              style={{
                whiteSpace: 'normal',
                wordBreak: 'break-word',
                maxWidth: '100%',
              }}
            >
              <User size={14} /> Pembayaran Melalui Agen
            </div>
          )
        } else {
          return <div>-</div>
        }
      },
    },
    {
      name: <span>Total</span>,
      minWidth: '150px',
      right: true,
      selector: (row) => row?.totalAmount ?? 0,
      cell: (row) => (
        <div className="text-end">
          Rp
          {intl.formatNumber(row?.totalAmount ?? 0, {
            useGrouping: 'always',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          })}
        </div>
      ),
    },
    {
      name: <span>Jumlah Item</span>,
      minWidth: '150px',
      right: true,
      cell: (row) => <div className="text-end">{row?.item?.length} item</div>,
    },
    {
      name: <span>Catatan</span>,
      wrap: true,
      cell: (row) => <ExpandableNoteList notes={row?.note} />,
      minWidth: '200px',
    },
  ]

  const ExpandedComponent = ({ data }) => {
    const imageFiles = data?.files?.filter((a) =>
      /\.(jpg|jpeg|png|gif|webp)$/i.test(a),
    )
    const pdfFiles = data?.files?.filter((a) => /\.pdf$/i.test(a))
    const primaryFile = imageFiles?.[0] || pdfFiles?.[0] || null
    const isPdf = /\.pdf$/i.test(primaryFile)
    return (
      <div className="m-3">
        {data?.files?.length > 0 && (
          <>
            <P>Bukti Bayar:</P>
            <div
              className="mb-2"
              style={{
                position: 'relative',
                width: '70px',
                height: '70px',
                overflow: 'hidden',
                borderRadius: '8px',
                marginTop: '8px',
                boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                border: '1px solid #f0f0f0',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                backgroundColor: '#f9f9f9',
                cursor: 'pointer',
              }}
              onClick={(event) => {
                setSelectedRow(event)
                setModalOpenImage(true)
              }}
            >
              {isPdf ? (
                <>
                  <div className="text-center">
                    <i
                      className="fa fa-file-pdf-o text-danger"
                      style={{ fontSize: 28 }}
                    ></i>
                    <div style={{ fontSize: '10px' }}>PDF</div>
                  </div>
                </>
              ) : (
                <img
                  src={primaryFile}
                  onError={(e) => {
                    e.currentTarget.onerror = null
                    e.currentTarget.src = ImageCrash
                  }}
                  alt="event-attachment"
                  loading="lazy"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    transition: 'transform 0.2s ease-in-out',
                  }}
                  onMouseOver={(e) =>
                    (e.currentTarget.style.transform = 'scale(1.05)')
                  }
                  onMouseOut={(e) =>
                    (e.currentTarget.style.transform = 'scale(1)')
                  }
                />
              )}

              {data?.files?.length - 1 > 0 && (
                <div
                  style={{
                    position: 'absolute',
                    top: '4px',
                    right: '4px',
                    width: '20px',
                    height: '20px',
                    borderRadius: '50%',
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                    color: 'white',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    fontSize: '10px',
                    fontWeight: 'bold',
                    border: '1px solid #fff',
                  }}
                >
                  +{data?.files?.length - 1}
                </div>
              )}
            </div>
          </>
        )}

        <P>Rincian Pembayaran:</P>
        {data?.item?.map((e, i) => {
          return (
            <div key={i}>
              <div className="border rounded p-3 mt-2 mb-2 bg-white d-flex justify-content-between align-items-center gap-2">
                <div className="d-flex align-items-center gap-2" style={{ minWidth: 0, flex: 1 }}>
                  <ProfilePic
                    imgSrc={e?.partnership?.logo}
                    altText={e?.name || '-'}
                    name={e?.name || '-'}
                    avatarSeed={e?.id}
                    width={28}
                    height={28}
                  />
                  <div style={{ minWidth: 0, flex: 1, lineHeight: '1.3' }}>
                    <P className="text-truncate mb-1 fw-medium">{e?.name || '-'}</P>
                    {e?.partnership?.name && (
                      <div className="text-primary fw-semibold text-truncate mb-1" style={{ fontSize: '0.8rem' }}>
                        {e?.partnership?.name}
                      </div>
                    )}
                    {e?.invoiceNumber ? <P className="text-truncate mb-1">{e?.invoiceNumber}</P> : null}
                    {e?.projectLabel ? <P className="text-truncate mb-0">{e?.projectLabel}</P> : null}
                  </div>
                </div>
                <P className="fw-medium text-end" style={{ flexShrink: 0 }}>
                  {' '}
                  Rp
                  {intl.formatNumber(e?.amountPaid ?? 0, {
                    useGrouping: 'always',
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                  })}
                </P>
              </div>
            </div>
          )
        })}
      </div>
    )
  }

  return (
    <>
      <PMBModalImagePreview
        isOpen={modalOpenImage}
        toggle={() => setModalOpenImage(!modalOpenImage)}
        images={
          Array.isArray(selectedRow?.paymentBulkHistory?.files)
            ? selectedRow?.paymentBulkHistory?.files
            : JSON.parse(selectedRow?.paymentBulkHistory?.files || '[]')
        }
        isDownload
      />
      <TableComponent
        expandableRows
        expandableRowsComponent={ExpandedComponent}
        sortServer
        defaultSortFieldId={1}
        onSort={handleSort}
        columns={columns}
        data={data?.items || []}
        progressPending={isLoading || status === 'pending'}
        pagination={true}
        paginationServer={false}
        paginationComponent={() => (
          <PMBPagination
            rowCount={data?.totalItems || 0}
            currentPage={tagPaymentHistory?.page || 0}
            rowsPerPage={tagPaymentHistory?.size || 10}
            setCurrentPage={(page) => {
              dispatch(
                setFetchOptionPaymentHistory({
                  page: page,
                }),
              )
            }}
            setRowsPerPage={(size) => {
              dispatch(
                setFetchOptionPaymentHistory({
                  size: size,
                }),
              )
            }}
          />
        )}
        subHeader
        subHeaderComponent={
          <div className="w-100 mb-3">
            {/* LEFT SIDE: Search */}

            <div
              className="d-flex align-items-center justify-content-end"
              style={{ gap: '12px' }}
            >
              <div className="d-flex gap-2">
                {activeFilterCount > 0 && (
                  <CustomButton
                    onClick={handleResetFilter}
                    color="danger"
                    className="d-flex align-items-center justify-content-center"
                    style={{
                      width: '40px',
                      height: '40px',
                      padding: 0,
                      minWidth: '40px',
                    }}
                  >
                    <SvgIcon name="x" size={16} color="white" />
                  </CustomButton>
                )}
                <CustomButton
                  onClick={() => setIsShowFilter((prevState) => !prevState)}
                  color="primary"
                  className="w-100 position-relative"
                  outline
                >
                  <div className="d-flex align-items-center gap-2 justify-content-center">
                    <Filter size={15} />
                    <P>Filter</P>
                    {activeFilterCount > 0 && (
                      <Badge
                        color="danger"
                        pill
                        style={{
                          position: 'absolute',
                          top: '-5px',
                          right: '-5px',
                          fontSize: '10px',
                        }}
                      >
                        {activeFilterCount}
                      </Badge>
                    )}
                    {isShowFilter ? (
                      <ChevronUp size={16} className="ml-2" />
                    ) : (
                      <ChevronDown size={16} className="ml-2" />
                    )}
                  </div>
                </CustomButton>
              </div>
            </div>

            <Collapse isOpen={isShowFilter} className="mt-3">
              <div
                className="border rounded p-3 bg-light mb-3 filter-collapse"
                style={{
                  width: '100%',
                  transition: 'all 0.3s ease-in-out',
                  opacity: isShowFilter ? 1 : 0,
                  // transform: isShowFilter ? 'translateY(0)' : 'translateY(-10px)',
                }}
              >
                <Row className="mt-3">
                  <Col xs={12} md={3} lg={3} className="mb-3">
                    <PMBSearchInputGroup
                      label="Cari"
                      value={searchInput}
                      placeholder="Cari Catatan..."
                      onChange={(e) => onHandleSearchChange(e.target.value)}
                      maxWidth="260px"
                      onKeyDown={handleKeyDown}
                    />
                  </Col>
                  <Col xs={12} md={3} lg={3} className="mb-3">
                    <p
                      className="mb-2"
                      style={{
                        display: 'inline-block',
                        whiteSpace: 'nowrap',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      Tanggal
                    </p>
                    <div
                      className="d-flex align-items-center border rounded"
                      style={{ padding: '0.135rem' }}
                    >
                      <Calendar size={17} className="ms-2" />
                      <Flatpickr
                        key={`${tagPaymentHistory?.from}-${tagPaymentHistory?.to}`}
                        value={[
                          new Date(tagPaymentHistory?.from),
                          new Date(tagPaymentHistory?.to),
                        ]}
                        options={{
                          mode: 'range',
                          dateFormat: 'd-m-Y',
                          onChange: (dates, str, instance) => {
                            if (dates.length === 1) {
                              const start = dates[0]
                              const today = moment().endOf('day').toDate()

                              const minDate = moment(start)
                                .subtract(90, 'days')
                                .startOf('day')
                                .toDate()
                              const maxDate = moment(start)
                                .add(90, 'days')
                                .endOf('day')
                                .toDate()

                              instance.set('minDate', minDate)
                              instance.set(
                                'maxDate',
                                maxDate > today ? today : maxDate,
                              )
                            }
                          },
                          onClose: (dates) => {
                            if (dates.length === 2) {
                              const [start, end] = dates
                              dispatch(
                                setFetchOptionPaymentHistory({
                                  from: moment(start)
                                    .startOf('day')
                                    .toISOString(),
                                  to: moment(end).endOf('day').toISOString(),
                                }),
                              )
                            }
                          },
                        }}
                        placeholder="Pilih Tanggal..."
                        className="form-control flat-picker bg-transparent border-0 shadow-none"
                      />
                      {tagPaymentHistory?.from || tagPaymentHistory?.to ? (
                        <div
                          className="px-2"
                          onClick={() => {
                            dispatch(
                              setFetchOptionPaymentHistory({
                                from: undefined,
                                to: undefined,
                              }),
                            )
                          }}
                        >
                          <X size={15} bold />
                        </div>
                      ) : null}
                    </div>
                  </Col>

                  <Col xs={12} md={3} lg={3} className="mb-3">
                    <p className="mb-2">Tipe</p>
                    <PMBSelect
                      isClearable={
                        tagPaymentHistory?.type === undefined ? false : true
                      }
                      style={{ width: '100%' }}
                      options={typeOptions}
                      value={
                        typeOptions.find(
                          (option) =>
                            String(option.value) ===
                            String(tagPaymentHistory?.type),
                        ) ?? undefined
                      }
                      placeholder="Pilih Tipe..."
                      onChange={(e) => {
                        // Client-side filtering: notify parent to update pagination tag

                        dispatch(
                          setFetchOptionPaymentHistory({
                            type: e?.value,
                          }),
                        )
                      }}
                    />
                  </Col>

                  <Col xs={12} md={3} lg={3} className="mb-3">
                    <p className="mb-2">Metode Pembayaran</p>
                    <PMBSelect
                      isClearable={
                        tagPaymentHistory?.paymentmethod === undefined
                          ? false
                          : true
                      }
                      style={{ width: '100%' }}
                      options={paymentMethodOptions}
                      value={
                        paymentMethodOptions.find(
                          (option) =>
                            String(option.value) ===
                            String(tagPaymentHistory?.paymentmethod),
                        ) ?? undefined
                      }
                      placeholder="Pilih Metode Pembayaran..."
                      onChange={(e) => {
                        dispatch(
                          setFetchOptionPaymentHistory({
                            paymentmethod: e?.value,
                          }),
                        )
                      }}
                    />
                  </Col>
                  <Col xs={12} md={3} lg={3} className="mb-3">
                    <p className="mb-2">Project</p>

                    <PMBProjectSelections
                      value={tagPaymentHistory?.projectId}
                      onChange={(e) => {
                        dispatch(
                          setFetchOptionPaymentHistory({
                            projectId: e?.value,
                          }),
                        )
                      }}
                      placeholder="Pilih project"
                    />
                  </Col>
                </Row>
              </div>
            </Collapse>
          </div>
        }
      />
    </>
  )
}

export default PaymentHistory
